/**
 * The pure geometry behind DetentSheet: detent heights, rubber-banding, release velocity, which detent a
 * release lands on, and a snap timing that continues the finger's speed. No DOM here, so it's unit-tested
 * (`__tests__/sheetMath.spec.ts`) and the component stays about events and styles.
 *
 * Heights are *visible* heights: px from the bottom of the layout viewport to the sheet's top edge.
 * Velocities are px/ms in screen coordinates: positive = moving down (the sheet getting shorter).
 */
import { EASE, IOS_BEZIER } from '@/motion/tokens'

export type DetentId = 'peek' | 'medium' | 'large' | 'fit' | number

export interface Detent {
    id: DetentId
    height: number
}

export interface SheetMetrics {
    /** Layout viewport height (window.innerHeight). */
    viewport: number
    safeTop: number
    safeBottom: number
    /** Grabber + header slot height. */
    header: number
    /** Natural height of the whole sheet for 'fit' (header + content + footer or bottom safe area). */
    fit: number
    /** Keyboard inset (0 when the sheet isn't keyboard-aware or no keyboard is up). */
    keyboard: number
}

/** Gap above a 'large' sheet, below the status bar (the page behind stays visible, like iOS). */
export const LARGE_TOP_GAP = 12
/** 'peek' shows the header: at least this tall, plus the bottom safe area. */
export const PEEK_MIN = 88
export const MEDIUM_RATIO = 0.5
/** A release faster than this (px/ms) is a flick: it moves one detent in its direction (or dismisses). */
export const FLICK_VELOCITY = 0.5
/** Velocity is measured over the last ~80ms of samples. */
export const VELOCITY_WINDOW_MS = 80
/** How far a resting sheet is pushed below the screen when dismissed (its shadow clears the edge). */
export const OFFSCREEN_EXTRA = 24
/** Initial slope of the iOS curve (y1 / x1): the speed it starts at, in "distance per duration". */
export const IOS_SLOPE = IOS_BEZIER[1] / IOS_BEZIER[0]

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export function largeHeight(m: Pick<SheetMetrics, 'viewport' | 'safeTop'>): number {
    return Math.max(0, Math.round(m.viewport - m.safeTop - LARGE_TOP_GAP))
}

/**
 * How much a keyboard-aware sheet grows (and its footer lifts) for the keyboard. The keyboard covers the
 * bottom safe area, so that part of the padding is reused rather than added.
 */
export function keyboardLift(keyboard: number, safeBottom: number): number {
    return Math.max(0, keyboard - safeBottom)
}

/** Visible height of one detent, capped at 'large'. Every detent but peek grows by the keyboard lift. */
export function detentHeight(id: DetentId, m: SheetMetrics): number {
    const large = largeHeight(m)
    if (id === 'large') return large
    if (id === 'peek') return Math.min(large, Math.round(Math.max(PEEK_MIN, m.header) + m.safeBottom))
    const base = id === 'medium' ? m.viewport * MEDIUM_RATIO : id === 'fit' ? m.fit : id
    return Math.min(large, Math.round(Math.max(0, base) + keyboardLift(m.keyboard, m.safeBottom)))
}

/** The detents as heights, shortest first (ties keep the order they were given in). Duplicated ids drop. */
export function resolveDetents(ids: readonly DetentId[], m: SheetMetrics): Detent[] {
    const seen = new Set<DetentId>()
    const list = (ids.length ? ids : (['large'] as DetentId[]))
        .filter((id) => (seen.has(id) ? false : (seen.add(id), true)))
        .map((id, order) => ({ id, height: detentHeight(id, m), order }))
    list.sort((a, b) => a.height - b.height || a.order - b.order)
    return list.map(({ id, height }) => ({ id, height }))
}

/** UIScrollView's rubber band: resistance grows with distance, never passing `dimension`. */
export function rubberband(distance: number, dimension: number, c = 0.55): number {
    if (distance <= 0 || dimension <= 0) return 0
    return (1 - 1 / ((distance * c) / dimension + 1)) * dimension
}

/** Map the finger's raw height to the sheet: rubber-band above the top detent (and below the lowest if it can't be dismissed). */
export function dragHeight(raw: number, lowest: number, highest: number, opts: { viewport: number; dismissible: boolean }): number {
    if (raw > highest) return highest + rubberband(raw - highest, opts.viewport)
    if (raw < lowest && !opts.dismissible) return lowest - rubberband(lowest - raw, opts.viewport)
    return Math.max(0, raw)
}

export function nearestIndex(heights: readonly number[], h: number): number {
    let best = 0
    for (let i = 1; i < heights.length; i++) if (Math.abs(heights[i] - h) < Math.abs(heights[best] - h)) best = i
    return best
}

/** Dragged this far below the lowest detent (after projection), a slow release dismisses. */
export function dismissDistance(lowest: number): number {
    return Math.min(lowest * 0.4, 240)
}

/**
 * Where a release lands: a detent index, or -1 to dismiss.
 * - A flick (|v| ≥ 0.5px/ms) goes one detent in its direction from where the sheet is; down from the lowest
 *   dismisses (or stays, when not dismissible). Up from the top stays at the top.
 * - Otherwise the nearest detent to where the sheet is heading (current position projected ~100ms).
 */
export function pickSnap(opts: { heights: readonly number[]; current: number; velocity: number; dismissible: boolean; flick?: number }): number {
    const { heights, velocity, dismissible } = opts
    const flick = opts.flick ?? FLICK_VELOCITY
    const n = heights.length
    if (!n) return -1
    const top = heights[n - 1]
    // Inside the rubber band the sheet is "at the top": a flick down from there moves one detent down.
    const current = clamp(opts.current, 0, top)
    if (velocity >= flick) {
        for (let i = n - 1; i >= 0; i--) if (heights[i] < current - 1) return i
        return dismissible ? -1 : 0
    }
    if (velocity <= -flick) {
        for (let i = 0; i < n; i++) if (heights[i] > current + 1) return i
        return n - 1
    }
    const projected = current - velocity * 100
    if (dismissible && projected < heights[0] - dismissDistance(heights[0])) return -1
    return nearestIndex(heights, projected)
}

/**
 * Snap duration: `base` normally, shorter when the finger was already moving fast (so the curve's start
 * matches its speed), never under `min`.
 * @param speed px/ms along the direction of travel (≥ 0).
 */
export function snapDuration(distance: number, speed: number, base: number, min = 180): number {
    if (distance < 1) return min
    const matching = speed > 0.01 ? (IOS_SLOPE * distance) / speed : Infinity
    return Math.round(clamp(matching, min, base))
}

/**
 * The iOS curve, steepened at the start when the release was faster than the curve would begin: the sheet
 * keeps the finger's speed instead of catching and re-accelerating.
 * @param speed px/ms along the direction of travel (≥ 0).
 */
export function snapCurve(distance: number, speed: number, duration: number): string {
    if (distance < 1 || speed <= 0) return EASE.ios
    const slope = (speed * duration) / distance
    if (slope <= IOS_SLOPE) return EASE.ios
    const y1 = Math.min(1, IOS_BEZIER[0] * slope)
    const x1 = y1 / slope
    return `cubic-bezier(${x1.toFixed(3)}, ${y1.toFixed(3)}, ${IOS_BEZIER[2]}, ${IOS_BEZIER[3]})`
}

/**
 * Scrim range [transparent at, opaque at] in visible heights, or null for no scrim.
 * - `'auto'`: no scrim at peek; it fades in between peek (or 0) and the lowest non-peek detent.
 * - `true`: fades in between 0 and the lowest detent. `false`: never.
 */
export function scrimRange(detents: readonly Detent[], mode: 'auto' | boolean): [number, number] | null {
    if (mode === false || !detents.length) return null
    if (mode === true) return [0, detents[0].height]
    const peek = detents.find((d) => d.id === 'peek')
    const lo = peek ? peek.height : 0
    const above = detents.find((d) => d.id !== 'peek' && d.height > lo)
    if (!above) return null
    return [lo, above.height]
}

export function scrimOpacity(h: number, range: [number, number] | null): number {
    if (!range) return 0
    const [lo, hi] = range
    if (hi <= lo) return h > lo ? 1 : 0
    return clamp((h - lo) / (hi - lo), 0, 1)
}

/**
 * Footer offset below its resting place (px, ≥ 0). A sticky footer sits at the bottom of the visible sheet,
 * but below the lowest non-peek detent it travels with the sheet (hidden at peek, sliding out on dismiss).
 */
export function footerDrop(h: number, detents: readonly Detent[]): number {
    const floor = detents.find((d) => d.id !== 'peek')?.height ?? detents[0]?.height ?? 0
    return Math.max(0, floor - h)
}

/** Release velocity from the last ~80ms of pointer samples. */
export class VelocityTracker {
    private samples: { t: number; y: number }[] = []

    constructor(private windowMs = VELOCITY_WINDOW_MS) {}

    add(t: number, y: number): void {
        this.samples.push({ t, y })
        const cutoff = t - this.windowMs * 3
        while (this.samples.length > 2 && this.samples[0].t < cutoff) this.samples.shift()
    }

    reset(): void {
        this.samples = []
    }

    /** px/ms, positive = moving down. 0 if the pointer rested more than 50ms before `now` (held, then lifted). */
    velocity(now?: number): number {
        const s = this.samples
        if (s.length < 2) return 0
        const last = s[s.length - 1]
        if (now !== undefined && now - last.t > 50) return 0
        let first = s[s.length - 2]
        for (let i = s.length - 2; i >= 0; i--) {
            if (last.t - s[i].t > this.windowMs) break
            first = s[i]
        }
        const dt = last.t - first.t
        return dt > 0 ? (last.y - first.y) / dt : 0
    }
}
